import { ActiveSubscription, BillingSummaryResponse, BillingPlan, BillingHistoryItem } from '../services/api'
import { PlanCardFeatureItem } from '../services/paymentsApi'
import { AI_REPLY_UNIT_PRICE_INR, GST_PERCENTAGE } from '../constants/payments'

export interface TopUpPricingBreakdown {
  baseAmount: number
  gstAmount: number
  totalAmount: number
  amountSubunits: number
}

export const calculateTopUpPricing = (credits: number): TopUpPricingBreakdown => {
  if (credits <= 0 || !Number.isInteger(credits)) {
    return {
      baseAmount: 0,
      gstAmount: 0,
      totalAmount: 0,
      amountSubunits: 0,
    }
  }

  const baseAmount = Number((credits * AI_REPLY_UNIT_PRICE_INR).toFixed(2))
  const gstAmount = Number((baseAmount * (GST_PERCENTAGE / 100)).toFixed(2))
  const totalAmount = Number((baseAmount + gstAmount).toFixed(2))
  const amountSubunits = Math.round(totalAmount * 100)

  return {
    baseAmount,
    gstAmount,
    totalAmount,
    amountSubunits,
  }
}

export function extractPlanFeatureItems(plan?: BillingPlan | null): PlanCardFeatureItem[] {
  const meta = plan?.display_metadata || {}

  if (meta.features && meta.features.length > 0) {
    return meta.features.map((f: any) =>
      typeof f === 'string'
        ? { text: f, included: true }
        : { ...f }
    )
  }

  return []
}

export interface GstCalculation {
  baseAmount: number
  gstAmount: number
  totalAmount: number
  formattedBase: string
  formattedGst: string
  formattedTotal: string
}

export function formatINR(amount: number, showDecimals: boolean = true): string {
  return `₹${amount.toLocaleString('en-IN', {
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: 2,
  })}`
}

export function calculateGst(baseAmount: number, gstPercentage: number = 18): GstCalculation {
  const gstAmount = baseAmount * (gstPercentage / 100)
  const totalAmount = baseAmount + gstAmount
  return {
    baseAmount,
    gstAmount,
    totalAmount,
    formattedBase: formatINR(baseAmount, true),
    formattedGst: formatINR(gstAmount, true),
    formattedTotal: formatINR(totalAmount, true),
  }
}

export function deriveSubscriptionState(summary: BillingSummaryResponse | null) {
  const activeSubscriptions = summary?.active_subscriptions || []
  const sortedSubs = [...activeSubscriptions].sort((a, b) => {
    const timeA = new Date(a.starts_at || 0).getTime()
    const timeB = new Date(b.starts_at || 0).getTime()
    return timeB - timeA
  })

  const activeSubscription: ActiveSubscription | null = sortedSubs.length > 0 ? sortedSubs[0] : null
  const hasGrowthSubscription = Boolean(summary?.has_active_growth_subscription)
  const isEssentials = !hasGrowthSubscription && Boolean(activeSubscription)
  const hasAnySubscription = Boolean(activeSubscription || summary?.has_active_subscription)
  const quotaBuckets = summary?.ai_quota_buckets || []
  // True when the active plan includes the AI automation (auto-reply) entitlement
  const hasAIAutomation = Boolean(summary?.effective_entitlements?.auto_reply_enabled)
  // Most recent pending transaction (if any)
  const pendingTransaction: BillingHistoryItem | null =
    summary?.billing_history?.find((h) => h.status === 'pending') ?? null
  const hasPendingCheckout = Boolean(pendingTransaction)

  return { activeSubscription, hasGrowthSubscription, isEssentials, hasAnySubscription, quotaBuckets, hasAIAutomation, hasPendingCheckout, pendingTransaction }
}

export function getPlanCardModeStyles(mode: 'standard' | 'featured' = 'standard') {
  switch (mode) {
    case 'featured':
      return {
        border: '2px solid transparent',
        backgroundImage:
          'linear-gradient(#ffffff, #ffffff), linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #60a5fa 100%)',
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
        boxShadow: '0 20px 40px -12px rgba(37, 99, 235, 0.18)',
        '&:hover': { transform: 'translateY(-6px)' },
      }
    case 'standard':
    default:
      return {
        border: '1px solid rgba(226, 232, 240, 0.9)',
        bgcolor: '#ffffff',
        '&:hover': { transform: 'translateY(-4px)', borderColor: 'rgba(37, 99, 235, 0.4)' },
      }
  }
}
